import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import ElmMark from "@/components/ElmMark";
import { POLICIES, POLICY_VERSION, policyBySlug } from "@/lib/policies";

export default function SecurityPolicy() {
  const { slug } = useParams();
  const { workspace } = useAuth();
  const policy = policyBySlug(slug);
  if (!policy) return <Navigate to="/workspace?section=security" replace />;
  const others = POLICIES.filter((p) => p.slug !== slug);
  return (
    <div className="min-h-screen bg-white text-stone-900" data-testid="policy-page">
      <div className="mx-auto flex max-w-[8.5in] items-center justify-between px-6 py-4 print:hidden">
        <Link to="/workspace?section=security" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="policy-back"><ArrowLeft className="h-4 w-4" />Back to Security</Link>
        <Button size="sm" className="rounded-full" onClick={() => window.print()} data-testid="policy-print"><Printer className="mr-1.5 h-3.5 w-3.5" />Print / Save PDF</Button>
      </div>
      <main className="mx-auto max-w-[8.5in] px-8 pb-12 pt-2 print:px-0 print:pt-0">
        <header className="flex items-start justify-between border-b border-stone-900 pb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.35em] text-stone-500">E.L.M platform policy · {POLICY_VERSION}</p>
            <h1 className="mt-1 font-serif text-3xl leading-none sm:text-4xl" data-testid="policy-title">{policy.title}</h1>
            <p className="mt-2 text-sm text-stone-600">{policy.summary}</p>
          </div>
          <ElmMark className="shrink-0" />
        </header>
        <dl className="mt-5 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          <div><dt className="text-[10px] uppercase tracking-[0.25em] text-stone-500">Purpose</dt><dd className="mt-0.5">{policy.purpose}</dd></div>
          <div><dt className="text-[10px] uppercase tracking-[0.25em] text-stone-500">Scope</dt><dd className="mt-0.5">{policy.scope}</dd></div>
        </dl>
        {policy.sections.map((s) => (
          <section key={s.heading} className="mt-5">
            <h2 className="text-base font-semibold">{s.heading}</h2>
            <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm leading-relaxed">{s.items.map((it) => <li key={it}>{it}</li>)}</ul>
          </section>
        ))}
        <section className="mt-6 rounded-md border border-stone-300 bg-stone-50 p-3 text-xs leading-relaxed print:bg-white" data-testid="policy-platform-note">
          <span className="font-semibold">Operated by the hosting platform: </span>{policy.platform}
        </section>
        <footer className="mt-6 flex flex-wrap justify-between gap-2 border-t border-stone-300 pt-3 text-xs text-stone-500">
          <span>Owner: {policy.owner}</span><span>{policy.review}</span>
          <span className="w-full">Applies to the workspace “{workspace?.name}” as a customer of E.L.M. This document is a platform policy and is the same for every workspace.</span>
        </footer>
        <nav className="mt-8 flex flex-wrap gap-3 text-sm print:hidden" data-testid="policy-others">
          {others.map((p) => <Link key={p.slug} to={`/workspace/policies/${p.slug}`} className="underline underline-offset-4" data-testid={`policy-link-${p.slug}`}>{p.title}</Link>)}
        </nav>
      </main>
    </div>
  );
}
